import React from 'react';
import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { TickCircle, TickSquare } from 'iconsax-react';
import { useApproveUserOperationMutation } from '../../store/services/business-requests';
import Button from '../atoms/Button';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';

interface ApproveModalProps {
  isModalOpen: boolean;
  handleModalClose: (value: boolean) => void;
  companyEmail: string;
  requestId: string;
}

const ApproveModal = ({
  isModalOpen,
  handleModalClose,
  companyEmail,
  requestId,
}: ApproveModalProps) => {
  const { t } = useTranslation();
  const [approveRequest] = useApproveUserOperationMutation();

  const handleApprove = async () => {
    try {
      await approveRequest({
        body: { email: companyEmail },
        id: requestId,
      }).unwrap();
      handleModalClose(false);
    } catch (error) {
      message.error('Something went wrong');
    }
  };

  const handleClose = () => {
    handleModalClose(false);
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleClose}
      closable={false}
      footer={null}
      centered
      destroyOnHidden
      width={600}
      data-test="approve-modal"
    >
      <div className="flex flex-col items-center justify-center gap-4 py-2" data-test="approve-modal-content">
        {/* Icon */}
        <div className="bg-green-50 w-12 h-12 rounded-xl flex justify-center items-center" data-test="approve-modal-icon">
          <TickSquare size={24} color="#16A34A" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-1 items-center text-center">
          <TitleText
            text={t('home.approveRequest')}
            className="!font-semibold"
          />
          <InfoText
            text={t('home.approveRequestDescription')}
            maxLength="FULL"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-center w-full gap-3 mt-2" data-test="approve-modal-actions">
          <Button type="button" variant="outline" onClick={handleClose} data-test="approve-modal-cancel-btn">
            {t('home.cancel')}
          </Button>
          <Button
            type="button"
            variant="success"
            icon={<TickCircle size={18} color="#ffffff" />}
            onClick={handleApprove}
            className="w-40"
            data-test="approve-modal-submit-btn"
          >
            {t('home.approve')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ApproveModal;
